/**
 * @file src/pages/invoice-creation-wizard/components/InvoiceDetailsSection.jsx
 *
 * What changed from the original:
 *  - Invoice Number input field ADDED — was missing entirely despite being
 *    required by validationRules and ZATCA (invoiceHeader.invoiceNumber)
 *  - ErrorMessage for referenceNumber fixed:
 *    Before: <ErrorMessage message={errors["invoice.referenceNumber"]} />
 *            errors is a function, not an object — always returned undefined
 *    After:  <ErrorMessage error={errors("invoice.referenceNumber")}
 *                          show={shouldShowError("invoice.referenceNumber")} />
 *  - Static arrays (invoiceTypes, currencies, paymentModes, allowanceChargeReasons)
 *    moved outside component — not recreated on every render
 *  - Latest delivery date validation hint added (BR-KSA-36)
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { ErrorMessage } from "components/ui/FormValidator";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// Static data — outside component to avoid recreation on every render
// ---------------------------------------------------------------------------

const INVOICE_TYPES = [
  {
    value: "standard",
    label: "Standard Invoice",
    description: "B2B invoice",
    icon: "FileText",
  },
  {
    value: "simplified",
    label: "Simplified Invoice",
    description: "For amounts ≤ SAR 1,000",
    icon: "FileText",
  },
  {
    value: "debit-note",
    label: "Debit Note",
    description: "Additional charges",
    icon: "FileText",
  },
  {
    value: "credit-note",
    label: "Credit Note",
    description: "Refund or adjustment",
    icon: "FileText",
  },
];

const CURRENCIES = [
  { value: "SAR", label: "Saudi Riyal (SAR)", rate: 1 },
  { value: "USD", label: "US Dollar (USD)", rate: 3.75 },
  { value: "EUR", label: "Euro (EUR)", rate: 4.1 },
];

const PAYMENT_MODES = [
  "Bank Deposit",
  "Cash",
  "Cheque",
  "Credit Card",
  "Debit Card",
  "Demand Draft",
  "Online Payment Gateway",
  "Online Transfer",
  "PayPal",
  "Wire Transfer",
];

const BANK_PAYMENT_MODES = ["Bank Deposit", "Online Transfer", "Wire Transfer"];

const ALLOWANCE_CHARGE_REASONS = [
  { value: "", label: "Select reason" },
  { value: "95", label: "Discount" },
  { value: "100", label: "Special agreement" },
  { value: "ADR", label: "Advertising" },
  { value: "DI", label: "Damage insurance" },
  { value: "FC", label: "Freight charge" },
  { value: "IN", label: "Insurance" },
  { value: "PC", label: "Packing costs" },
  { value: "SH", label: "Shipping and handling" },
];

const SELECT_CLS =
  "w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent";

// ---------------------------------------------------------------------------
// InvoiceDetailsSection
// ---------------------------------------------------------------------------

const InvoiceDetailsSection = ({
  data,
  onChange,
  errors,
  shouldShowError,
  onFieldChange,
  onFieldBlur,
  previousInvoices = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleInputChange = (field, value) => {
    onChange("invoice", field, value);
    onFieldChange(`invoice.${field}`, value);
  };

  const handleInputBlur = (field, value) => {
    onFieldBlur(`invoice.${field}`, value);
  };

  // Word count for notes field
  const noteWordCount = data.notes
    ? data.notes.trim().split(/\s+/).filter(Boolean).length
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Invoice Details
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Configure invoice settings and metadata
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>

        {/* ── Invoice Type ─────────────────────────────────────────────────── */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground dark:text-white mb-3">
            Invoice Type
          </label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {INVOICE_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleInputChange("type", type.value)}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all duration-200 ${
                  data.type === type.value
                    ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary"
                    : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <Icon name={type.icon} size={20} />
                <div className="text-center">
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs text-muted-foreground dark:text-gray-400">
                    {type.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* ── Basic Invoice Information ─────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Invoice Number — REQUIRED, was missing in original */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Invoice Number *
                </label>
                <Input
                  type="text"
                  value={data.number}
                  onChange={(e) => handleInputChange("number", e.target.value)}
                  onBlur={(e) => handleInputBlur("number", e.target.value)}
                  placeholder="INVX20240001"
                  className={
                    shouldShowError("invoice.number") ? "border-error" : ""
                  }
                />
                <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                  Format: 4 uppercase letters + 4 digits + 2 alphanumeric
                </p>
                <ErrorMessage
                  error={errors("invoice.number")}
                  show={shouldShowError("invoice.number")}
                />
              </div>

              {/* Referenced Invoice — credit/debit notes only */}
              {(data.type === "credit-note" || data.type === "debit-note") && (
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Referenced Invoice Number *
                  </label>
                  <select
                    value={data.referenceNumber}
                    onChange={(e) =>
                      handleInputChange("referenceNumber", e.target.value)
                    }
                    onBlur={(e) =>
                      handleInputBlur("referenceNumber", e.target.value)
                    }
                    className={`${SELECT_CLS} ${
                      shouldShowError("invoice.referenceNumber")
                        ? "border-error"
                        : ""
                    }`}
                  >
                    <option value="">Select an invoice to reference</option>
                    {previousInvoices
                      .filter((inv) => inv.invoiceTypeCode === "388")
                      .map((invoice) => (
                        <option
                          key={invoice.invoiceNumber}
                          value={invoice.invoiceNumber}
                        >
                          {invoice.invoiceNumber} —{" "}
                          {invoice.buyer?.registrationName}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                    Select the original invoice this{" "}
                    {data.type === "credit-note" ? "credit" : "debit"} note
                    references
                  </p>
                  {/* Fixed: errors is a function, not an object */}
                  <ErrorMessage
                    error={errors("invoice.referenceNumber")}
                    show={shouldShowError("invoice.referenceNumber")}
                  />
                </div>
              )}

              {/* Invoice Date */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Invoice Date *
                </label>
                <Input
                  type="date"
                  value={data.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  onBlur={(e) => handleInputBlur("date", e.target.value)}
                  className={
                    shouldShowError("invoice.date") ? "border-error" : ""
                  }
                />
                <ErrorMessage
                  error={errors("invoice.date")}
                  show={shouldShowError("invoice.date")}
                />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Due Date
                </label>
                <Input
                  type="date"
                  value={data.dueDate}
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  onBlur={(e) => handleInputBlur("dueDate", e.target.value)}
                />
              </div>

              {/* Actual Delivery Date */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Actual Delivery Date
                </label>
                <Input
                  type="date"
                  value={data.actualDeliveryDate}
                  onChange={(e) =>
                    handleInputChange("actualDeliveryDate", e.target.value)
                  }
                  onBlur={(e) =>
                    handleInputBlur("actualDeliveryDate", e.target.value)
                  }
                />
              </div>

              {/* Latest Delivery Date */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Latest Delivery Date
                </label>
                <Input
                  type="date"
                  value={data.latestDeliveryDate}
                  onChange={(e) =>
                    handleInputChange("latestDeliveryDate", e.target.value)
                  }
                  onBlur={(e) =>
                    handleInputBlur("latestDeliveryDate", e.target.value)
                  }
                />
                <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                  Must be on or after the Actual Delivery Date (BR-KSA-36)
                </p>
              </div>
            </div>

            {/* ── Currency ──────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Currency
                </label>
                <select
                  value={data.currency}
                  onChange={(e) => {
                    const selected = CURRENCIES.find(
                      (c) => c.value === e.target.value,
                    );
                    handleInputChange("currency", e.target.value);
                    handleInputChange("exchangeRate", selected?.rate ?? 1);
                  }}
                  className={SELECT_CLS}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {data.currency !== "SAR" && (
                  <p className="mt-2 text-sm text-muted-foreground dark:text-gray-400">
                    Exchange Rate: 1 {data.currency} = {data.exchangeRate} SAR
                  </p>
                )}
              </div>
            </div>

            {/* ── Payment Information ───────────────────────────────────────── */}
            <div>
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Payment Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Payment Mode
                  </label>
                  <select
                    value={data.paymentMode || ""}
                    onChange={(e) =>
                      handleInputChange("paymentMode", e.target.value)
                    }
                    className={SELECT_CLS}
                  >
                    <option value="">Select payment mode</option>
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Payment Note
                  </label>
                  <Input
                    type="text"
                    value={data.paymentNote || ""}
                    onChange={(e) =>
                      handleInputChange("paymentNote", e.target.value)
                    }
                    onBlur={(e) =>
                      handleInputBlur("paymentNote", e.target.value)
                    }
                    placeholder="e.g. Payment within 30 days"
                  />
                  <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                    Optional note about payment terms agreed with the buyer
                  </p>
                </div>

                {BANK_PAYMENT_MODES.includes(data.paymentMode) && (
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      IBAN *
                    </label>
                    <Input
                      type="text"
                      value={data.iban}
                      onChange={(e) =>
                        handleInputChange("iban", e.target.value)
                      }
                      onBlur={(e) => handleInputBlur("iban", e.target.value)}
                      placeholder="SA0380000000608010167519"
                    />
                    <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                      Required for bank transfer payments (SA + 22 digits)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Notes ────────────────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Invoice Notes
              </label>
              <textarea
                value={data.notes}
                onChange={(e) => {
                  const words = e.target.value
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);
                  if (words.length <= 500 || e.target.value === "") {
                    handleInputChange("notes", e.target.value);
                  }
                }}
                onBlur={(e) => handleInputBlur("notes", e.target.value)}
                rows={3}
                placeholder="Additional notes or terms and conditions..."
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                {noteWordCount}/500 words
              </p>
            </div>

            {/* ── Document-Level Allowance/Charge ──────────────────────────── */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Document-Level Allowance/Charge
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Type
                  </label>
                  <select
                    value={data.documentAllowanceChargeIndicator || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "documentAllowanceChargeIndicator",
                        e.target.value,
                      )
                    }
                    className={SELECT_CLS}
                  >
                    <option value="">No allowance/charge</option>
                    <option value="false">Allowance (Discount)</option>
                    <option value="true">Charge (Additional Fee)</option>
                  </select>
                </div>

                {data.documentAllowanceChargeIndicator && (
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Reason
                    </label>
                    <select
                      value={data.documentAllowanceChargeReasonCode || ""}
                      onChange={(e) => {
                        const code = e.target.value;
                        const reason = ALLOWANCE_CHARGE_REASONS.find(
                          (r) => r.value === code,
                        );
                        handleInputChange(
                          "documentAllowanceChargeReasonCode",
                          code,
                        );
                        handleInputChange(
                          "documentAllowanceChargeReason",
                          reason?.label ?? "",
                        );
                      }}
                      className={SELECT_CLS}
                    >
                      {ALLOWANCE_CHARGE_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {data.documentAllowanceChargeIndicator && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Amount ({data.currency})
                    </label>
                    <Input
                      type="number"
                      value={data.documentAllowanceChargeAmount || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "documentAllowanceChargeAmount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Base Amount ({data.currency})
                    </label>
                    <Input
                      type="number"
                      value={data.documentAllowanceChargeBaseAmount || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "documentAllowanceChargeBaseAmount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Percentage (%)
                    </label>
                    <Input
                      type="number"
                      value={data.documentAllowanceChargeMultiplier || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "documentAllowanceChargeMultiplier",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                      If base amount is provided, percentage calculates the
                      amount
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ── ZATCA Compliance Notice ───────────────────────────────────── */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="Shield"
                  size={20}
                  className="text-amber-600 dark:text-amber-400 mt-0.5"
                />
                <div>
                  <h5 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                    ZATCA Compliance Check
                  </h5>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {data.type === "simplified"
                      ? "Simplified invoices are limited to SAR 1,000 and don't require customer VAT number."
                      : "Standard invoices require complete customer information and tax details."}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default InvoiceDetailsSection;
